import {
    doc,
    getDoc,
    updateDoc,
    Timestamp,
    query,
    where,
    getDocs,
    collectionGroup
} from 'firebase/firestore';
import { db } from '../firebase/config';

export interface UserProgress {
    userId?: string; // Added for collection group queries
    completionStatus: boolean;
    completedAt?: Timestamp | null;
    isFavorited?: boolean; // Added for favorites functionality
    notes?: {
        content: string;
        created: Timestamp;
        lastUpdated: Timestamp;
    } | null;
    lastAccessed?: any;
}

class ProgressService {
    // Fetch user progress for a single document
    async fetchUserProgress(userId: string, docId: string, collectionPath?: string): Promise<UserProgress | null> {
        try {
            const progressPath = this.buildUserDataPath(collectionPath, docId, userId);
            const progressRef = doc(db, progressPath);
            const progressDoc = await getDoc(progressRef);

            if (progressDoc.exists()) {
                const data = progressDoc.data() as UserProgress;

                // Migration: Add userId if missing
                if (!data.userId && userId) {
                    await updateDoc(progressRef, { userId });
                }

                return data;
            }

            return null;
        } catch (error) {
            console.error('Error fetching user progress:', error);
            throw error;
        }
    }

    // Batch fetch all user progress data across all courses
    async batchFetchAllUserProgress(userId: string): Promise<Map<string, UserProgress>> {
        try {


            // Security rules only allow reading userData docs whose userId matches
            // the signed-in user, so this filter is required for the query to succeed.
            const userDataQuery = query(
                collectionGroup(db, 'userData'),
                where('userId', '==', userId)
            );

            const snapshot = await getDocs(userDataQuery);

            const progressMap = new Map<string, UserProgress>();

            snapshot.docs.forEach((docSnapshot, index) => {
                const userData = docSnapshot.data() as UserProgress;
                const parentRef = docSnapshot.ref.parent.parent!;
                const parentDocId = parentRef.id;
                const parentPath = parentRef.path;

                // Create a unique key that matches the getProgress lookup
                // For root level: "courses/docId"
                // For nested: "collectionPath/docId"
                let key: string;
                if (parentPath === `courses/${parentDocId}`) {
                    // Root level document
                    key = `courses/${parentDocId}`;
                } else {
                    // Nested document - extract the collection path
                    const pathParts = parentPath.split('/');
                    const collectionPath = pathParts.slice(0, -1).join('/'); // Remove the last part (docId)
                    key = `${collectionPath}/${parentDocId}`;
                }



                progressMap.set(key, userData);
            });



            return progressMap;
        } catch (error) {
            console.error('🔴 progressService: Error batch fetching all user progress:', error);
            throw error;
        }
    }

    // Build the full path for userData based on collectionPath and docId
    private buildUserDataPath(collectionPath: string | undefined, docId: string, userId: string): string {
        if (collectionPath) {
            // collectionPath is like: /courses/bhagavadGita/children/sararthaVarsini/children/bgSvCh1/children
            // We need to create: courses/bhagavadGita/children/sararthaVarsini/children/bgSvCh1/children/{docId}/userData/{userId}
            const cleanPath = collectionPath.startsWith('/') ? collectionPath.substring(1) : collectionPath;
            return `${cleanPath}/${docId}/userData/${userId}`;
        }
        // Fallback to old structure if no collectionPath provided
        return `courses/${docId}/userData/${userId}`;
    }
}

const progressService = new ProgressService();
export default progressService; 